'use client';

import '../globals.css';
import 'react-datepicker/dist/react-datepicker.css';
import { usePathname } from 'next/navigation';
import { useState, useEffect } from 'react';
import { SessionProvider } from 'next-auth/react';
import AppUpdateChecker from '@/components/AppUpdateChecker';
import { titles } from '@/app/pageTitles';
import { getDomainShareMeta } from '@/utils/domainShareMeta';

/**
 * @param {{ children: import('react').ReactNode, session?: object, initialHostname?: string, shareMeta?: object }} props
 */
export default function Providers({ children, session, initialHostname = '', shareMeta = null }) {
  const pathname = usePathname();
  const [hostname, setHostname] = useState(initialHostname || '');

  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hostname) {
      setHostname(window.location.hostname);
    }
  }, []);

  const meta = shareMeta?.host === hostname || !hostname
    ? shareMeta || getDomainShareMeta(hostname || initialHostname)
    : getDomainShareMeta(hostname || initialHostname);

  const [state, setState] = useState({ title: meta?.siteName || 'Exodo Panel' });

  useEffect(() => {
    const pathnameWithNumber = pathname.replace(/\/\d+/g, '');
    const defaultTitle = meta?.siteName || 'Exodo Panel';
    const title = titles[pathnameWithNumber] || defaultTitle;
    setState({ title });
  }, [pathname, meta?.siteName]);

  const isLeadScaleDomain = meta?.theme === 'leadscale';
  const icons = meta?.icons || getDomainShareMeta('exodopanel.com').icons;
  const icon16 = icons.icon?.find((i) => i.sizes === '16x16')?.url || '/favicon-16x16.png';
  const icon32 = icons.icon?.find((i) => i.sizes === '32x32')?.url || '/favicon-32x32.png';
  const iconDefault = icons.icon?.[icons.icon.length - 1]?.url || '/favicon.ico';
  const apple = icons.apple?.[0]?.url || '/apple-touch-icon.png';
  const ogImageAbs = `${meta?.origin || ''}${meta?.ogImage || '/og/exodo-og.png'}`;

  return (
    <html
      lang="en"
      className="h-full bg-gray-50 overflow-x-hidden"
      style={
        pathname === '/' &&
        !pathname.startsWith('/landing-page') &&
        !isLeadScaleDomain
          ? {
              backgroundImage: 'url(exodopanel-background.jpg)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }
          : isLeadScaleDomain &&
              (pathname === '/' ||
                pathname.startsWith('/login') ||
                pathname.startsWith('/register') ||
                pathname.startsWith('/forgot-password'))
            ? { background: '#07131f' }
            : {}
      }
    >
      <head>
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-280R8WJCDR"></script>
        <script
          dangerouslySetInnerHTML={{
            __html: `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'G-280R8WJCDR');
          ${process.env.NEXT_PUBLIC_BUILD_TIME ? `window.__NEXT_BUILD_TIME = '${process.env.NEXT_PUBLIC_BUILD_TIME}';` : ''}
        `,
          }}
        />
        {/* Open Graph / Twitter — also emitted via generateMetadata for crawlers */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={meta?.siteName || 'Exodo Panel'} />
        <meta property="og:title" content={meta?.title || state.title} />
        <meta property="og:description" content={meta?.description || ''} />
        <meta property="og:url" content={meta?.origin || ''} />
        <meta property="og:image" content={ogImageAbs} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta?.title || state.title} />
        <meta name="twitter:description" content={meta?.description || ''} />
        <meta name="twitter:image" content={ogImageAbs} />
        <meta name="description" content={meta?.description || ''} />
      </head>
      <body>
        <link rel="apple-touch-icon" sizes="180x180" href={apple} />
        <link rel="icon" type="image/png" sizes="32x32" href={icon32} />
        <link rel="icon" type="image/png" sizes="16x16" href={icon16} />
        <link rel="icon" href={iconDefault} />
        <link rel="manifest" href={meta?.manifest || '/site.webmanifest'} />

        <meta name="msapplication-TileColor" content={meta?.tileColor || '#da532c'} />
        <meta name="theme-color" content={meta?.themeColor || '#ffffff'} />
        <title>{state.title}</title>

        <AppUpdateChecker />
        <SessionProvider session={session} refetchOnWindowFocus={false} refetchInterval={0}>
          {children}
        </SessionProvider>
      </body>
    </html>
  );
}
